/**
 * Multi-currency display: always show amount with currency.
 * API can return money as { amount_minor, currency_code, amount_major } or legacy number (assume NPR).
 * See docs/MULTI_CURRENCY_PRICING_DESIGN.md
 */

const CURRENCY_DISPLAY = {
  NPR: { symbol: 'Rs', decimals: 0, locale: 'en-IN' },
  USD: { symbol: '$', decimals: 2, locale: 'en-US' },
};

/**
 * Format a money object from API.
 * @param {Object} money - { amount_major, currency_code } or { amount_minor, currency_code }
 * @returns {string} e.g. "Rs 100" or "$100.00"
 */
export function formatMoney(money) {
  if (!money || money.currency_code == null) return '—';
  const amountMajor = money.amount_major != null
    ? Number(money.amount_major)
    : (money.amount_minor != null ? Number(money.amount_minor) / 100 : 0);
  const currencyCode = money.currency_code;
  const config = CURRENCY_DISPLAY[currencyCode] || { symbol: currencyCode, decimals: 2, locale: 'en' };
  const value = amountMajor.toLocaleString(config.locale, {
    minimumFractionDigits: config.decimals,
    maximumFractionDigits: config.decimals,
  });
  return config.symbol === '$' ? `${config.symbol}${value}` : `${config.symbol} ${value}`;
}

/**
 * Format from minor units (e.g. paisa/cents) when API only sends amount_minor + currency_code.
 */
export function formatMoneyFromMinor(amountMinor, currencyCode) {
  if (amountMinor == null || currencyCode == null) return '—';
  const decimals = currencyCode === 'USD' ? 2 : 2;
  const major = (Number(amountMinor) / 100).toFixed(decimals);
  return formatMoney({ amount_major: parseFloat(major), currency_code: currencyCode });
}

/**
 * Backward compatibility: format legacy numeric amount (assume NPR when no currency).
 */
export function formatMoneyLegacy(amount, currencyCode = 'NPR') {
  if (amount == null) return '—';
  return formatMoney({ amount_major: Number(amount), currency_code: currencyCode });
}

/** Sum map like { USD: 138, NPR: 5000 } → "$138.00 · Rs 5,000" */
export function formatAmountsByCurrency(amounts) {
  if (!amounts || typeof amounts !== 'object') return '—';
  const parts = Object.entries(amounts)
    .filter(([, value]) => Number(value) > 0)
    .map(([code, value]) => formatMoneyLegacy(value, code));
  return parts.length ? parts.join(' · ') : '—';
}

/**
 * Supported listing currencies for vendor dropdown.
 */
export const SUPPORTED_CURRENCIES = [
  { code: 'NPR', label: 'NPR (Nepalese Rupee)' },
  { code: 'USD', label: 'USD (US Dollar)' },
];
